import React, { useState, useEffect } from 'react';
import { theme } from '../utils/theme';
import api from '../utils/ApiManager';

/**
 * DiscountField component for managing a single discount entry.
 */
const DiscountField = ({ threshold, discount, onChange, onDelete }) => {
  return (
    <div className="flex gap-3 mb-2 items-center">
      <div className="flex-1">
        <label className="block text-sm font-medium text-[#0C3B2E]">Threshold</label>
        <input
          type="number"
          min="1"
          value={threshold}
          onChange={(e) => onChange('threshold', parseInt(e.target.value, 10) || 0)}
          className={`${theme.input.base} w-full`}
          placeholder="Quantity"
        />
      </div>
      <div className="flex-1">
        <label className="block text-sm font-medium text-[#0C3B2E]">Discount (%)</label>
        <input
          type="number"
          min="0"
          max="100"
          value={discount}
          onChange={(e) => onChange('discount', parseInt(e.target.value, 10) || 0)}
          className={`${theme.input.base} w-full`}
          placeholder="Discount %"
        />
      </div>
      <div className="flex items-end pb-1">
        <button 
          type="button"
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 transition-colors p-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/**
 * DiscountSection component to manage an entire discount category.
 */
const DiscountSection = ({ title, description, discounts, onChange }) => {
  // Convert the discounts object to an array of { threshold, discount } pairs for rendering
  const discountEntries = Object.entries(discounts || {}).map(([threshold, discount]) => ({
    threshold: parseInt(threshold, 10),
    discount
  }));

  const handleAddDiscount = () => {
    // Create a new discount entry - find a unique threshold value
    const thresholds = Object.keys(discounts || {}).map(Number);
    let newThreshold = 1;
    // Make sure we're not duplicating an existing threshold
    while(thresholds.includes(newThreshold)) {
      newThreshold++;
    }
    
    // Create a new object with the added threshold
    const updatedDiscounts = {
      ...(discounts || {}),
      [newThreshold]: 5 // Default 5% discount for new entries
    };
    
    onChange(updatedDiscounts);
  };

  const handleDiscountChange = (index, field, value) => {
    const entry = discountEntries[index];
    const oldThreshold = entry.threshold;
    
    // Create a new object without the old entry
    const { [oldThreshold]: removed, ...restDiscounts } = discounts;
    
    // Add the updated entry
    if (field === 'threshold') {
      // For threshold changes, we need to make sure the new threshold doesn't already exist
      const newThreshold = value;
      if (Object.keys(discounts).map(Number).includes(newThreshold) && newThreshold !== oldThreshold) {
        // Alert the user about the duplicate threshold
        alert(`Threshold ${newThreshold} already exists. Please choose another value.`);
        return;
      }
      onChange({
        ...restDiscounts,
        [newThreshold]: entry.discount
      });
    } else {
      // For discount value changes
      onChange({
        ...restDiscounts,
        [oldThreshold]: value
      });
    }
  };

  const handleDeleteDiscount = (index) => {
    const thresholdToRemove = discountEntries[index].threshold;
    const { [thresholdToRemove]: removed, ...restDiscounts } = discounts;
    onChange(restDiscounts);
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-[#0C3B2E] mb-2">{title}</h3>
      <p className="text-sm text-gray-600 mb-4">{description}</p>
      
      {discountEntries.map((entry, index) => (
        <DiscountField
          key={index}
          threshold={entry.threshold}
          discount={entry.discount}
          onChange={(field, value) => handleDiscountChange(index, field, value)}
          onDelete={() => handleDeleteDiscount(index)}
        />
      ))}
      
      <button
        type="button"
        onClick={handleAddDiscount}
        className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.sm} mt-2`}
      >
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add New Discount Level
        </div>
      </button>
    </div>
  );
};

/**
 * Main PricingEditor component
 */
const PricingEditor = () => {
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch current pricing data
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/api/pricing');
        setPricingData(response.data.data);
      } catch (err) {
        setError('Failed to load pricing data: ' + (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Changed endpoint from /api/admin/pricing to /api/pricing/admin
      const response = await api.post('/api/pricing/admin', pricingData);
      
      // Update the local state with the returned data
      setPricingData(response.data.data);
      setSuccess('Pricing updated successfully');
    } catch (err) {
      setError('Failed to update pricing: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle field changes
  const handleBasePriceChange = (e) => {
    setPricingData({
      ...pricingData,
      basePrice: parseFloat(e.target.value) || 0
    });
  };

  const handleVariantPriceChange = (e) => {
    setPricingData({
      ...pricingData,
      variantPrice: parseFloat(e.target.value) || 0
    });
  };

  const handleCurrencyChange = (e) => {
    setPricingData({
      ...pricingData,
      currency: e.target.value
    });
  };

  const handleDiscountChange = (type, newDiscounts) => {
    setPricingData({
      ...pricingData,
      [type]: newDiscounts
    });
  };

  if (loading && !pricingData) {
    return (
      <div className={`${theme.card.base} mb-8`}>
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !pricingData) {
    return (
      <div className={`${theme.card.base} mb-8 border-red-500`}>
        <div className="text-red-500">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className={`${theme.button.base} ${theme.button.primary} ${theme.button.sizes.sm} mt-4`}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`${theme.card.base}`}>
      <h2 className="text-xl font-semibold text-[#0C3B2E] mb-6">Pricing Configuration</h2>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6" role="alert">
          <p>{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-[#0C3B2E] mb-2">Base Price per Image ($)</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={pricingData?.basePrice || 0}
              onChange={handleBasePriceChange}
              className={`${theme.input.base} w-full`}
              placeholder="Base price"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0C3B2E] mb-2">Price per Variant ($)</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={pricingData?.variantPrice || 0}
              onChange={handleVariantPriceChange}
              className={`${theme.input.base} w-full`}
              placeholder="Variant price"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0C3B2E] mb-2">Currency</label>
            <select
              value={pricingData?.currency || 'USD'}
              onChange={handleCurrencyChange}
              className={`${theme.input.base} w-full`}
              required
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="HUF">HUF (Ft)</option>
            </select>
          </div>
        </div>

        <DiscountSection
          title="Volume Discounts"
          description="Discounts applied based on the total number of images uploaded"
          discounts={pricingData?.volumeDiscounts}
          onChange={(newDiscounts) => handleDiscountChange('volumeDiscounts', newDiscounts)}
        />

        <DiscountSection
          title="Package Discounts"
          description="Discounts applied to the total order based on image quantity"
          discounts={pricingData?.packageDiscounts}
          onChange={(newDiscounts) => handleDiscountChange('packageDiscounts', newDiscounts)}
        />

        <DiscountSection
          title="Variant Discounts"
          description="Discounts applied when ordering multiple variants of the same image"
          discounts={pricingData?.variantDiscounts}
          onChange={(newDiscounts) => handleDiscountChange('variantDiscounts', newDiscounts)}
        />

        <div className="mt-6">
          <button
            type="submit"
            className={`${theme.button.base} ${theme.button.primary} ${theme.button.sizes.md}`}
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </div>
            ) : 'Save Pricing Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PricingEditor;